import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import { HygraphTopics, TOPIC_MAP } from 'src/utils/hygraph'
import { IPost } from '../../types/hygraph'
import Pagination from '../Pagination'
import BlogCard from './BlogCard'
import BlogHero from './BlogHero'
import BlogSocial from './BlogSocial'
import CategorySection from './CategorySection'

type IProps = {
  firstPost: IPost | null
  posts: IPost[]
  totalPosts: number
  page: number
  pageSize: number
  categories: { id: string; name: string; path: string }[]
  selectedCategory: HygraphTopics | null
  basePath: string
}

function BlogManager({
  firstPost,
  posts,
  totalPosts,
  page,
  pageSize,
  categories = [],
  selectedCategory,
  basePath
}: IProps) {
  const { t } = useTranslation('blog')

  const pageCount =
    Math.floor(totalPosts / pageSize) + (totalPosts % pageSize === 0 ? 0 : 1)

  const { pillar, label } = selectedCategory
    ? TOPIC_MAP[selectedCategory]
    : { pillar: null, label: null }

  return (
    <div className="bg-white-500">
      {/* Editorial header — 12-col rail */}
      <header className="mx-auto w-full max-w-7xl px-5 pt-[calc(var(--nav-h)+40px)] pb-10 md:px-10 md:pt-[calc(var(--nav-h)+64px)] md:pb-14">
        {pillar && label ? (
          <div className="md:grid md:grid-cols-12 md:gap-12">
            <div className="md:col-span-7">
              <Link
                href={`/${pillar}`}
                className="group inline-flex items-center gap-1.5 font-display text-[11px] tracking-[0.22em] text-ink-200 uppercase transition-colors hover:text-blue-500 md:text-xs"
              >
                <span
                  aria-hidden="true"
                  className="transition-transform duration-200 group-hover:-translate-x-0.5"
                >
                  ←
                </span>
                {t('CATEGORY.BACK_TO_PILLAR', { label })}
              </Link>
              <p className="mt-6 font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:mt-8 md:text-xs">
                {t('CATEGORY.EYEBROW')}
              </p>
              <h1 className="mt-4 max-w-2xl font-display text-[32px] leading-[1.08] font-medium tracking-[-0.015em] text-ink-700 md:mt-5 md:text-[56px]">
                {`${t('CATEGORY.TITLE_INTRO')} `}
                <span className="font-display font-normal text-blue-500 italic">
                  {label.toLowerCase()}.
                </span>
              </h1>
            </div>
          </div>
        ) : (
          <div className="md:grid md:grid-cols-12 md:gap-12">
            <div className="md:col-span-8">
              <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                {t('INDEX.EYEBROW')}
              </p>
              <h1 className="mt-4 max-w-3xl font-display text-[32px] leading-[1.06] font-medium tracking-[-0.018em] text-ink-700 md:mt-5 md:text-[56px] lg:text-[64px]">
                {`${t('INDEX.TITLE.TEXT_DARK')} `}
                <span className="font-display font-normal text-blue-500 italic">
                  {t('INDEX.TITLE.TEXT_BLUE')}
                </span>
              </h1>
            </div>
            <p className="mt-6 max-w-md text-[14px] leading-relaxed text-ink-200 md:col-span-4 md:mt-0 md:self-end md:text-[15px]">
              {t('INDEX.SUBTITLE')}
            </p>
          </div>
        )}
      </header>

      <main className="mx-auto w-full max-w-7xl px-5 pb-24 md:px-10 md:pb-32">
        {/* Featured post */}
        <BlogHero firstPost={firstPost} />

        {/* Category filter — eyebrow links, not pills */}
        <div className="mt-16 md:mt-24">
          <CategorySection
            categories={categories}
            selectedCategory={selectedCategory}
          />
        </div>

        {/* Grid */}
        {!posts || posts.length === 0 ? (
          <div className="border-t border-ink-100/15 py-20 md:py-28">
            <div className="md:grid md:grid-cols-12 md:gap-12">
              <div className="md:col-span-4">
                <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {t('GRID.EYEBROW')}
                </p>
              </div>
              <div className="mt-6 md:col-span-8 md:mt-0">
                <h2 className="font-display text-[26px] leading-[1.15] font-medium text-ink-700 md:text-[32px]">
                  {t('EMPTY.TITLE')}
                </h2>
                <p className="mt-3 max-w-md text-[14px] leading-relaxed text-ink-200 md:text-[15px]">
                  {t('EMPTY.DESC')}
                </p>
              </div>
            </div>
          </div>
        ) : (
          <section className="mt-10 md:mt-14">
            <div className="md:grid md:grid-cols-12 md:gap-12">
              <div className="md:col-span-4">
                <p className="font-display text-[11px] tracking-[0.22em] text-blue-500 uppercase md:text-xs">
                  {t('GRID.EYEBROW')}
                </p>
              </div>
              <div className="md:col-span-8" />
            </div>

            <div className="mt-8 grid grid-cols-1 gap-12 md:mt-12 md:grid-cols-2 md:gap-x-10 md:gap-y-16 lg:grid-cols-3">
              {posts.map((post) => (
                <BlogCard key={post?.title} post={post} />
              ))}
            </div>

            {totalPosts > pageSize && (
              <div className="mt-16 flex justify-center border-t border-ink-100/15 pt-10 md:mt-24 md:pt-14">
                <Pagination
                  count={pageCount}
                  createHref={(p) =>
                    p === 1 ? `${basePath}` : `${basePath}/page/${p}`
                  }
                  page={page}
                />
              </div>
            )}
          </section>
        )}

        {/* Social CTA — light editorial */}
        <div className="mt-20 md:mt-28">
          <BlogSocial />
        </div>
      </main>
    </div>
  )
}

export default BlogManager
